import { geocode } from '../../services/mapboxService';
import { normalizeEpRoutes, type EpJourney, type EpRouteCandidate } from '../../functions/src/common/epAdvanceV2';

const PACE_ROLES = ['Primary', 'Alternate', 'Contingency', 'Emergency'];
const ROUTE_FIELDS = ['description', 'distanceMeters', 'durationMillis', 'staticDurationMillis', 'path', 'legs'];
const ROUTE_MODIFIERS: google.maps.routes.RouteModifiers[] = [
    {}, { avoidHighways: true }, { avoidTolls: true }, { avoidHighways: true, avoidTolls: true },
    { avoidFerries: true }, { avoidHighways: true, avoidFerries: true }, { avoidTolls: true, avoidFerries: true },
    { avoidHighways: true, avoidTolls: true, avoidFerries: true },
];

function pathKey(path: google.maps.LatLngAltitudeLiteral[]): string {
    const points: Array<{ lat: number; lng: number }> = [];
    for (const location of path) {
        if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng) || Math.abs(location.lat) > 90 || Math.abs(location.lng) > 180)
            return '';
        const point = { lat: Math.round(location.lat * 100000), lng: Math.round(location.lng * 100000) };
        const previous = points[points.length - 1];
        if (previous?.lat === point.lat && previous.lng === point.lng)
            continue;
        while (points.length > 1) {
            const start = points[points.length - 2];
            const end = points[points.length - 1];
            const dx = end.lng - start.lng;
            const dy = end.lat - start.lat;
            const nextDx = point.lng - end.lng;
            const nextDy = point.lat - end.lat;
            if (dx * nextDy !== dy * nextDx || dx * nextDx + dy * nextDy < 0)
                break;
            points.pop();
        }
        points.push(point);
    }
    let first = 2166136261;
    let second = 5381;
    for (const point of points) {
        const key = `${point.lat},${point.lng};`;
        for (let index = 0; index < key.length; index++) {
            first = Math.imul(first ^ key.charCodeAt(index), 16777619);
            second = Math.imul(second, 33) ^ key.charCodeAt(index);
        }
    }
    return points.length > 1 ? `g:${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}` : '';
}

function detourWaypoints(path: google.maps.LatLngAltitudeLiteral[], distanceMeters: number): google.maps.routes.Waypoint[] {
    if (path.length < 2 || !pathKey(path))
        return [];
    const start = path[0];
    const end = path[path.length - 1];
    const waypoints: google.maps.routes.Waypoint[] = [];
    const offset = Math.max(250, Math.min(distanceMeters * 0.15, 5000));
    for (const fraction of [1 / 3, 2 / 3]) {
        const point = path[Math.round((path.length - 1) * fraction)];
        const longitudeScale = 111320 * Math.max(0.05, Math.cos(point.lat * Math.PI / 180));
        const dx = (end.lng - start.lng) * longitudeScale;
        const dy = (end.lat - start.lat) * 111320;
        const length = Math.hypot(dx, dy);
        if (length < 1)
            continue;
        for (const side of [-1, 1, -2, 2]) {
            const lat = point.lat + dx / length * offset * side / 111320;
            const lng = ((point.lng - dy / length * offset * side / longitudeScale + 540) % 360) - 180;
            if (Math.abs(lat) <= 90)
                waypoints.push({ location: { lat, lng }, via: true });
        }
    }
    return waypoints;
}

function routingError(failure: unknown): string {
    const error = failure as { code?: string; status?: string; message?: string } | null;
    return String(error?.code || error?.status || error?.message || 'Unknown routing failure')
        .replace(/https?:\/\/\S+|(?:key|token)\s*[=:]\s*\S+/gi, '[redacted]').slice(0, 120);
}

export async function collectEpJourneyRoutes(journey: EpJourney, computeRoutes: typeof google.maps.routes.Route.computeRoutes) {
    let origin: google.maps.routes.ComputeRoutesRequest['origin'] = journey.origin;
    let destination: google.maps.routes.ComputeRoutesRequest['destination'] = journey.destination;
    let routes: EpRouteCandidate[] = [];
    let primaryPath: google.maps.LatLngAltitudeLiteral[] = [];
    let blocked = false;
    const errors = new Set<string>();
    const search = async (routeModifiers: google.maps.routes.RouteModifiers, waypoint?: google.maps.routes.Waypoint) => {
        try {
            const response = await computeRoutes({ origin, destination, travelMode: 'DRIVING', routingPreference: 'TRAFFIC_AWARE',
                computeAlternativeRoutes: !waypoint, routeModifiers, fields: ROUTE_FIELDS,
                ...(waypoint ? { intermediates: [waypoint] } : {}) });
            for (const route of response.routes || []) {
                if (waypoint && (Number(route.distanceMeters) > routes[0].distanceMeters * 2 ||
                    route.legs?.some(leg => (leg.steps || []).some(step => step.maneuver?.startsWith('UTURN')))))
                    continue;
                const steps = (route.legs || []).flatMap(leg => (leg.steps || []).map(step => String(step.instructions || ''))).filter(Boolean);
                const key = pathKey(route.path || []);
                const candidate = { label: '', description: String(route.description || steps[0] || (key ? 'Mapped driving route' : '')), steps,
                    distanceMeters: Number(route.distanceMeters), durationMinutes: Math.ceil(Number(route.durationMillis) / 60000),
                    staticMinutes: Math.ceil(Number(route.staticDurationMillis) / 60000), ...(key ? { pathKey: key } : {}) };
                const next = normalizeEpRoutes([...routes, candidate]);
                if (next.length === routes.length)
                    continue;
                if (!routes.length)
                    primaryPath = route.path || [];
                routes = next;
                if (routes.length === 4)
                    break;
            }
        } catch (failure) {
            const error = routingError(failure);
            errors.add(error);
            blocked = /\b(REQUEST_DENIED|PERMISSION_DENIED|UNAUTHENTICATED|OVER_QUERY_LIMIT|RESOURCE_EXHAUSTED)\b/i.test(error);
        }
    };
    await search(ROUTE_MODIFIERS[0]);
    if (!routes.length && !blocked) {
        const endpoints = await Promise.allSettled([geocode(journey.origin), geocode(journey.destination)]);
        if (endpoints[0].status === 'fulfilled' && endpoints[1].status === 'fulfilled') {
            origin = { lat: endpoints[0].value.lat, lng: endpoints[0].value.lng };
            destination = { lat: endpoints[1].value.lat, lng: endpoints[1].value.lng };
            await search(ROUTE_MODIFIERS[0]);
            if (!endpoints[0].value.isExact || !endpoints[1].value.isExact)
                errors.add('Journey endpoint geocoding is approximate; confirm both addresses');
        } else
            errors.add('Journey endpoint geocoding failed; confirm both addresses');
    }
    for (let index = 1; index < ROUTE_MODIFIERS.length && routes.length < 4 && !blocked; index++)
        await search(ROUTE_MODIFIERS[index]);
    for (const waypoint of detourWaypoints(primaryPath, routes[0]?.distanceMeters || 0)) {
        if (routes.length === 4 || blocked)
            break;
        await search({}, waypoint);
    }
    for (let index = 0; index < routes.length; index++)
        routes[index].label = PACE_ROLES[index];
    return { routes, errors: [...errors] };
}
